import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "@mui/material";
import { getFinOpsDashboard, clearFinOpsMessages } from "../../store/finOps/finOpsSlice";
import getOrgCode from "../../helpers/jwt-token-access/getOrgCode";
import FinOpsHeader from "./Components/FinOpsHeader";
import FinOpsFilters from "./Components/FinOpsFilters";
import FinOpsDashboardContent from "./Components/FinOpsDashboardContent";
import FinOpsLoadingState from "./Components/FinOpsLoadingState";
import FinOpsErrorState from "./Components/FinOpsErrorState";

const FinOpsDashboard = () => {
  const dispatch = useDispatch();
  
  // Get initial orgCode from helper function
  const initialOrgCode = getOrgCode();
  
  // Filter state - initialize with orgCode from helper
  const [selectedOrgCode, setSelectedOrgCode] = useState(initialOrgCode || "");
  const [selectedAccountNumber, setSelectedAccountNumber] = useState("");
  
  // Track if we've synced with API response (to avoid overriding user selections)
  const hasSyncedWithApi = useRef(false);
  // Track if initial load is complete to prevent filter effect from running on mount
  const isInitialLoad = useRef(true);
  // Track if we're updating from API sync to prevent triggering filter effect
  const isSyncingFromApi = useRef(false);

  // Selectors
  const {
    isGettingFinOpsDashboard,
    getFinOpsDashboardError,
    getFinOpsDashboardMessage,
    finOpsDashboardData,
  } = useSelector((state) => state.finOps);

  // Get orgAccountMap and selectedOrgCode from data
  const orgAccountMap = finOpsDashboardData?.orgAccountMap || {};
  const apiSelectedOrgCode = finOpsDashboardData?.selectedOrgCode;

  // Sync selectedOrgCode with API response only on initial load
  useEffect(() => {
    if (apiSelectedOrgCode && !hasSyncedWithApi.current) {
      isSyncingFromApi.current = true;
      setSelectedOrgCode(apiSelectedOrgCode);
      hasSyncedWithApi.current = true;
    }
  }, [apiSelectedOrgCode]);
  
  // Reset sync flag after state has been updated
  useEffect(() => {
    if (isSyncingFromApi.current) {
      const timer = setTimeout(() => {
        isSyncingFromApi.current = false;
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [selectedOrgCode]);

  // Fetch dashboard data
  const fetchDashboardData = useCallback((orgCode, accountNumber) => {
    const payload = {};
    if (orgCode) {
      payload.orgCode = orgCode;
    }
    if (accountNumber) {
      payload.accountNumber = accountNumber;
    }
    dispatch(getFinOpsDashboard(payload));
  }, [dispatch]);

  // Initial load - use orgCode from helper function
  useEffect(() => {
    // On first load, use orgCode from getOrgCode() helper
    const initialOrgCode = getOrgCode();
    if (initialOrgCode) {
      fetchDashboardData(initialOrgCode, "");
    } else {
      // If no orgCode in localStorage, make blank call
      fetchDashboardData("", "");
    }
    
    // Mark initial load as complete after a short delay
    setTimeout(() => {
      isInitialLoad.current = false;
    }, 100);
    
    // Cleanup: clear messages on unmount
    return () => {
      dispatch(clearFinOpsMessages());
    };
  }, [dispatch, fetchDashboardData]); // Only run on mount

  // Fetch data when filters change (but not on initial load or API sync)
  useEffect(() => {
    // Skip if it's initial load or we're syncing from API
    if (isInitialLoad.current || isSyncingFromApi.current) {
      return;
    }
    
    if (selectedOrgCode || selectedAccountNumber) {
      // Debounce the API call
      const timeoutId = setTimeout(() => {
        fetchDashboardData(selectedOrgCode, selectedAccountNumber);
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [selectedOrgCode, selectedAccountNumber, fetchDashboardData]);

  // Handle organization change
  const handleOrgChange = (orgCode) => {
    setSelectedOrgCode(orgCode);
    // Reset account when org changes
    setSelectedAccountNumber("");
  };

  // Handle account change
  const handleAccountChange = (accountNumber) => {
    setSelectedAccountNumber(accountNumber);
  };

  useEffect(() => {
    document.title = 'FinOps - Customer Summary';
  }, []);

  return (
    <div className="page-content">
      <Box
        sx={{
          px: { xs: 2, sm: 3, md: 4 },
          py: 3,
          maxWidth: "100%",
          width: "100%",
        }}
      >
        <FinOpsHeader
          orgAccountMap={orgAccountMap}
          selectedOrgCode={selectedOrgCode}
          selectedAccountNumber={selectedAccountNumber}
          onOrgChange={handleOrgChange}
          onAccountChange={handleAccountChange}
        />

        <Box sx={{ mt: 3 }}>
          {isGettingFinOpsDashboard ? (
            <FinOpsLoadingState />
          ) : getFinOpsDashboardError ? (
            <FinOpsErrorState error={getFinOpsDashboardError} />
          ) : (
            <FinOpsDashboardContent data={finOpsDashboardData} />
          )}
        </Box>
      </Box>
    </div>
  );
};

export default FinOpsDashboard;
